import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const TYPE_LABEL = { permis: "Permis exigés", zone: "Risques liés au lieu", coactivite: "Coactivité" };

export default function Regles() {
  const { permisMeta } = useApp();
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [edit, setEdit] = useState(null);
  const canEdit = ["HSE", "ADMINISTRATEUR"].includes(user?.role);

  const load = () => api.get("/regles").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const toggleActif = async (r) => {
    try { await api.put(`/regles/${r.code}`, { actif: !r.actif }); load(); toast.success(`${r.code} ${!r.actif ? "activée" : "désactivée"}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const save = async () => {
    try { await api.put(`/regles/${edit.code}`, { nom: edit.nom, severite: edit.severite, msg: edit.msg, fix: edit.fix }); setEdit(null); load(); toast.success("Règle mise à jour"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const groups = ["permis", "zone", "coactivite"];

  return (
    <div>
      <div className="crumb">Configuration · moteur de règles</div>
      <h1>Bibliothèque de règles</h1>
      {!canEdit && <div className="notice" data-testid="regles-readonly">Consultation seule — un profil HSE / Administrateur est requis pour modifier les règles.</div>}

      {groups.map((g) => (
        <div key={g}>
          <h2 style={{ fontSize: "1rem", margin: "16px 0 8px", color: "var(--text-muted)" }}>{TYPE_LABEL[g]}</h2>
          <div className="tablewrap"><table>
            <thead><tr><th style={{ width: 90 }}>Code</th><th>Règle</th><th style={{ width: 110 }}>Sévérité</th><th style={{ width: 90 }}>Active</th><th style={{ width: 70 }}></th></tr></thead>
            <tbody>
              {rules.filter((r) => r.type === g).map((r) => (
                <tr key={r.code} data-testid={`regle-${r.code}`}>
                  <td className="mono">{r.code}</td>
                  <td><b>{r.nom}</b><div style={{ fontSize: ".76rem", color: "var(--text-muted)", marginTop: 2 }}>{r.msg}{r.permis && ` → ${permisMeta[r.permis]?.nom}`}</div></td>
                  <td><span className={`badge ${r.severite === "bloquant" ? "b-incompatible" : "b-soumis"}`}>{r.severite === "bloquant" ? "Bloquant" : "Alerte"}</span></td>
                  <td>
                    <label style={{ display: "inline-flex", width: "auto" }}>
                      <input type="checkbox" style={{ width: "auto" }} checked={r.actif} disabled={!canEdit} data-testid={`regle-toggle-${r.code}`} onChange={() => toggleActif(r)} />
                    </label>
                  </td>
                  <td>{canEdit && <button className="iconbtn" onClick={() => setEdit({ ...r })} data-testid={`regle-edit-${r.code}`}><Pencil size={13} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      ))}

      {edit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={() => setEdit(null)}>
          <div className="card" style={{ width: 520, maxWidth: "92vw", margin: 0 }} onClick={(e) => e.stopPropagation()} data-testid="regle-editor">
            <h3 style={{ fontSize: "1.05rem" }}>Éditer {edit.code}</h3>
            <label className="f">Nom</label>
            <input value={edit.nom} onChange={(e) => setEdit({ ...edit, nom: e.target.value })} />
            <label className="f">Sévérité</label>
            <select value={edit.severite} onChange={(e) => setEdit({ ...edit, severite: e.target.value })}><option value="bloquant">Bloquant</option><option value="alerte">Alerte</option></select>
            <label className="f">Message</label>
            <textarea rows={2} value={edit.msg} onChange={(e) => setEdit({ ...edit, msg: e.target.value })} />
            <label className="f">Chemin de déblocage</label>
            <textarea rows={2} value={edit.fix || ""} onChange={(e) => setEdit({ ...edit, fix: e.target.value })} />
            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <button className="btn btn-p" onClick={save} data-testid="regle-save">Enregistrer</button>
              <button className="btn btn-g" onClick={() => setEdit(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
